/**
 * API Client for Chatbot Alpilink
 *
 * Handles all communication with the FastAPI backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Chatbot,
  ChatbotCreate,
  ChatbotUpdate,
  Conversation,
  ConversationCreate,
  ConversationUpdate,
  ConversationChatRequest,
  Document,
  Message,
  ChatRequest,
  ChatResponse,
  UserSession,
  PaginatedResponse,
  PaginationParams,
  HealthResponse,
  AppInfoResponse,
  ApiError,
  SourceDocument,
  ElicitationInterrupt,
  ElicitationResponse,
  Reservation,
  AuthUser,
} from '@/types/api';

/** Events yielded by the SSE chat streams (see backend api/conversations.py `_stream_turn`). */
export type ChatStreamEvent =
  | string
  | { sources: SourceDocument[] }
  | { conv: { id: string; title: string } }
  | { clarification: string }
  | { interrupt: ElicitationInterrupt }
  | { reservation: Reservation }
  /** The agent corrected the text it streamed (e.g. an unverified phone number) */
  | { replace: string }
  /** What the assistant is doing while the customer waits (never stored) */
  | { status: string };

class ApiClient {
  private client: AxiosInstance;
  private sessionToken: string | null = null;
  private unauthorizedHandlers = new Set<() => void>();

  constructor(baseURL: string = '/api') {
    this.client = axios.create({
      baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add session token
    this.client.interceptors.request.use(
      (config) => {
        if (this.sessionToken) {
          config.headers['X-Session-Token'] = this.sessionToken;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ detail?: string }>) => {
        if (error.response?.status === 401 && !error.config?.url?.startsWith('/auth/')) {
          this.unauthorizedHandlers.forEach((handler) => handler());
        }
        const apiError: ApiError = {
          error: error.message,
          detail: error.response?.data?.detail || error.response?.statusText,
          code: error.code || error.response?.status?.toString(),
        };
        return Promise.reject(apiError);
      }
    );
  }

  setSessionToken(token: string) {
    this.sessionToken = token;
  }

  clearSessionToken() {
    this.sessionToken = null;
  }

  // ==================== Authentication ====================

  /** Called whenever the API answers 401. Returns an unsubscribe function. */
  onUnauthorized(handler: () => void): () => void {
    this.unauthorizedHandlers.add(handler);
    return () => {
      this.unauthorizedHandlers.delete(handler);
    };
  }

  async login(email: string, password: string): Promise<AuthUser> {
    const response = await this.client.post<AuthUser>('/auth/login', { email, password });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }

  async getCurrentUser(): Promise<AuthUser> {
    const response = await this.client.get<AuthUser>('/auth/me');
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // ==================== Health & Info ====================

  async getHealth(): Promise<HealthResponse> {
    const response = await this.client.get<HealthResponse>('/health');
    return response.data;
  }

  async getAppInfo(): Promise<AppInfoResponse> {
    const response = await this.client.get<AppInfoResponse>('/info');
    return response.data;
  }

  // ==================== Chatbots ====================

  async getChatbots(params?: PaginationParams): Promise<PaginatedResponse<Chatbot>> {
    const response = await this.client.get<PaginatedResponse<Chatbot>>('/chatbots/', { params });
    return response.data;
  }

  async getChatbot(id: string): Promise<Chatbot> {
    const response = await this.client.get<Chatbot>(`/chatbots/${id}`);
    return response.data;
  }

  async createChatbot(chatbot: ChatbotCreate): Promise<Chatbot> {
    const response = await this.client.post<Chatbot>('/chatbots/', chatbot);
    return response.data;
  }

  async updateChatbot(id: string, updates: ChatbotUpdate): Promise<Chatbot> {
    const response = await this.client.put<Chatbot>(`/chatbots/${id}`, updates);
    return response.data;
  }

  async deleteChatbot(id: string): Promise<void> {
    await this.client.delete(`/chatbots/${id}`);
  }

  async clearChatbotData(id: string): Promise<Chatbot> {
    const response = await this.client.post<Chatbot>(`/chatbots/${id}/clear`);
    return response.data;
  }

  async getChatbotStats(id: string): Promise<any> {
    const response = await this.client.get(`/chatbots/${id}/stats`);
    return response.data;
  }

  // ==================== Documents ====================

  async getDocuments(
    conversationId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Document>> {
    const response = await this.client.get<PaginatedResponse<Document>>(
      `/documents/by-conversation/${conversationId}`,
      { params }
    );
    return response.data;
  }

  async uploadLocalFiles(conversationId: string, files: FileList): Promise<Document[]> {
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });

    const response = await this.client.post<Document[]>(
      `/documents/upload-local/${conversationId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await this.client.delete(`/documents/${id}`);
  }

  /** Transcribe a voice note (16 kHz mono WAV, see utils/audio). */
  async transcribe(audio: Blob): Promise<string> {
    const formData = new FormData();
    formData.append('audio', audio, 'note.wav');

    const response = await this.client.post<{ text: string }>('/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.text;
  }

  async downloadDocument(id: string): Promise<Blob> {
    const response = await this.client.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDocumentStats(chatbotId: string): Promise<any> {
    const response = await this.client.get(`/documents/stats/${chatbotId}`);
    return response.data;
  }

  // ==================== Chat ====================

  async chat(request: ChatRequest): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>(
      `/chat/${request.chatbot_id}`,
      request
    );
    return response.data;
  }

  async *chatStream(request: ChatRequest): AsyncGenerator<string | { sources: SourceDocument[] }, void, unknown> {
    const streamBaseUrl = '/api';
    const response = await fetch(`${streamBaseUrl}/chat/${request.chatbot_id}/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 401) this.unauthorizedHandlers.forEach((handler) => handler());
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              return;
            } else if (data.startsWith('[ERROR]')) {
              throw new Error(data.slice(7));
            } else if (data.startsWith('[SOURCES]')) {
              try {
                const sources: SourceDocument[] = JSON.parse(data.slice(9));
                yield { sources };
              } catch (e) {
                console.warn('Failed to parse sources:', e);
              }
            } else {
              try {
                yield JSON.parse(data);
              } catch {
                yield data;
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async getChatHistory(
    chatbotId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Message>> {
    const response = await this.client.get<PaginatedResponse<Message>>(
      `/chat/${chatbotId}/messages`,
      { params }
    );
    return response.data;
  }

  async clearChatHistory(chatbotId: string): Promise<void> {
    await this.client.delete(`/chat/${chatbotId}/messages`);
  }

  async createMessage(chatbotId: string, message: any): Promise<Message> {
    const response = await this.client.post<Message>(
      `/chat/${chatbotId}/messages`,
      message
    );
    return response.data;
  }

  async checkChatbotReady(chatbotId: string): Promise<any> {
    const response = await this.client.get(`/chat/${chatbotId}/ready`);
    return response.data;
  }

  // ==================== Conversations ====================

  async getConversations(
    chatbotId?: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Conversation>> {
    const queryParams: Record<string, any> = { ...params };
    if (chatbotId) queryParams.chatbot_id = chatbotId;
    const response = await this.client.get<PaginatedResponse<Conversation>>(
      '/conversations',
      { params: queryParams }
    );
    return response.data;
  }

  async getReservation(id: string): Promise<Reservation> {
    const response = await this.client.get<Reservation>(`/reservations/${encodeURIComponent(id)}`);
    return response.data;
  }

  async getConversation(id: string): Promise<Conversation> {
    const response = await this.client.get<Conversation>(`/conversations/${id}`);
    return response.data;
  }

  async createConversation(chatbotId: string, data?: ConversationCreate): Promise<Conversation> {
    const response = await this.client.post<Conversation>(
      `/chatbots/${chatbotId}/conversations`,
      data || {}
    );
    return response.data;
  }

  async renameConversation(id: string, update: ConversationUpdate): Promise<Conversation> {
    const response = await this.client.patch<Conversation>(
      `/conversations/${id}`,
      update
    );
    return response.data;
  }

  async deleteConversation(id: string): Promise<void> {
    await this.client.delete(`/conversations/${id}`);
  }

  async getConversationMessages(
    conversationId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Message>> {
    const response = await this.client.get<PaginatedResponse<Message>>(
      `/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data;
  }

  async *conversationChatStream(
    conversationId: string,
    request: ConversationChatRequest
  ): AsyncGenerator<ChatStreamEvent, void, unknown> {
    yield* this.streamSSE(`/api/conversations/${conversationId}/chat/stream`, request);
  }

  /** Answer a pending MCP confirmation ({ interrupt } event) and stream the rest of the turn. */
  async *conversationResumeStream(
    conversationId: string,
    responses: Record<string, ElicitationResponse>
  ): AsyncGenerator<ChatStreamEvent, void, unknown> {
    yield* this.streamSSE(`/api/conversations/${conversationId}/resume`, { responses });
  }

  private async *streamSSE(url: string, body: unknown): AsyncGenerator<ChatStreamEvent, void, unknown> {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401) this.unauthorizedHandlers.forEach((handler) => handler());
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    // SSE frames can be split across chunks: keep the unterminated tail
    let buffer = '';

    const parseTagged = <T,>(data: string, tag: string): T | null => {
      try {
        return JSON.parse(data.slice(tag.length)) as T;
      } catch (e) {
        console.warn(`Failed to parse ${tag} event:`, e);
        return null;
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') {
            return;
          } else if (data.startsWith('[ERROR]')) {
            throw new Error(data.slice(7));
          } else if (data.startsWith('[SOURCES]')) {
            const sources = parseTagged<SourceDocument[]>(data, '[SOURCES]');
            if (sources) yield { sources };
          } else if (data.startsWith('[CONV]')) {
            const conv = parseTagged<{ id: string; title: string }>(data, '[CONV]');
            if (conv) yield { conv };
          } else if (data.startsWith('[CLARIFICATION]')) {
            const c = parseTagged<{ message?: string }>(data, '[CLARIFICATION]');
            if (c) yield { clarification: c.message || '' };
          } else if (data.startsWith('[INTERRUPT]')) {
            const interrupt = parseTagged<ElicitationInterrupt>(data, '[INTERRUPT]');
            if (interrupt) yield { interrupt };
          } else if (data.startsWith('[RESERVATION]')) {
            const reservation = parseTagged<Reservation>(data, '[RESERVATION]');
            if (reservation) yield { reservation };
          } else if (data.startsWith('[STATUS]')) {
            const s = parseTagged<{ text: string }>(data, '[STATUS]');
            if (s?.text) yield { status: s.text };
          } else if (data.startsWith('[REPLACE]')) {
            const r = parseTagged<{ text: string }>(data, '[REPLACE]');
            if (r) yield { replace: r.text };
          } else {
            try {
              yield JSON.parse(data);
            } catch {
              yield data;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // ==================== Authentication (Future) ====================

  async createSession(userAgent?: string, ipAddress?: string): Promise<UserSession> {
    const response = await this.client.post<UserSession>('/auth/session', {
      user_agent: userAgent,
      ip_address: ipAddress,
    });
    return response.data;
  }

  async getSession(sessionToken: string): Promise<UserSession> {
    const response = await this.client.get<UserSession>(`/auth/session/${sessionToken}`);
    return response.data;
  }

  async deactivateSession(sessionToken: string): Promise<void> {
    await this.client.delete(`/auth/session/${sessionToken}`);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

export default apiClient;